import { useEffect, useRef, useState } from 'react'
import { subscribe } from './appsync-subscription.js'

/**
 * React hook that establishes an AppSync WebSocket subscription for the lifetime
 * of the component (or until enabled becomes false / variables change).
 *
 * @param {string} query       GraphQL subscription document
 * @param {object} variables
 * @param {function} onData    Called with the subscription data payload
 * @param {object} options
 * @param {boolean} options.enabled  Set to false to skip subscribing (default: true)
 * @returns {{ connected: boolean }} Whether the subscription WebSocket is active
 */
export function useSubscription(query, variables, onData, { enabled = true } = {}) {
  const [connected, setConnected] = useState(false)

  // Keep onData stable so callers don't need to memoize it
  const onDataRef = useRef(onData)
  onDataRef.current = onData

  // Re-subscribe only when query, stringified variables, or enabled change
  const variablesKey = JSON.stringify(variables)

  useEffect(() => {
    if (!enabled) {
      setConnected(false)
      return
    }
    const unsub = subscribe(query, variables, (data) => onDataRef.current(data), {
      onStatus: ({ connected: c }) => setConnected(c),
    })
    return () => {
      unsub()
      setConnected(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, variablesKey, enabled])

  return { connected }
}
